import React, { useState, useEffect, useCallback } from "react";
import { request } from "../../../utils/api";
import { toast } from "react-hot-toast";
import { EditIcon, DeleteIcon, PlusIcon, SpinnerIcon } from "../../../Icons";
import {
  displayDateUS,
  formatSignature,
  formatDateForInput,
  toIsoDate,
  getCurrentLADate,
  formatLATimestampForDate,
} from "../../../utils/dateUtils";
import {
  SERVICE_LOG_TYPES,
  SERVICE_LOG_TYPE_LABELS,
} from "../../../constants/serviceLog";
import DeleteConfirmModal from "./DeleteConfirmModal";
import DocumentBulkActions from "./DocumentBulkActions";
import { useRowSelection } from "../hooks/useRowSelection";
import { printSelectedServiceLogs } from "../../../print/documentPrintService";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { isSurveyorUser } from "../../../utils/documentPermissions";
import InputDatePicker from "../../EvaluationNew/components/InputDatePicker";
import {
  DOCUMENT_THEME,
  DOCUMENT_BTN,
  DOCUMENT_MODAL,
  DOCUMENT_COL,
  DOCUMENT_ROLE_CHIP,
  SERVICE_LOG_FIELD,
} from "../documentTheme";
import { FORM_FIELD } from "../../../ui/formTheme";

const getServiceLogRowKey = (row) => String(row.id);

const ServiceLog = ({
  employeeId,
  tenantId,
  isAdmin,
  isEmployeeInactive = false,
  employeeName = "",
  employeeEmail = "",
  companyName = "",
  logoUrl = "",
}) => {
  const { fullName, isSurveyRole, user: sessionUser } = useCurrentUser();
  const isViewOnly =
    isSurveyRole || isSurveyorUser(sessionUser) || isEmployeeInactive;
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkPrintLoading, setBulkPrintLoading] = useState(false);
  const [createDate, setCreateDate] = useState(() =>
    displayDateUS(getCurrentLADate()),
  );
  const [createServiceType, setCreateServiceType] = useState(
    "monthly_in_service_training",
  );
  const [createNote, setCreateNote] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editServiceType, setEditServiceType] = useState(
    "monthly_in_service_training",
  );
  const [editNote, setEditNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [itemToSign, setItemToSign] = useState(null);
  const [isSigning, setIsSigning] = useState(false);
  const [signName, setSignName] = useState("");

  const isSelectable = useCallback(() => true, []);
  const selection = useRowSelection(details, getServiceLogRowKey, isSelectable);

  const fetchServiceLog = async () => {
    if (!employeeId || !tenantId) return;
    setLoading(true);
    try {
      const response = await request(
        `/service-log-list?employeeId=${employeeId}&tenantId=${tenantId}`,
      );
      if (response.status === 1 && response.result?.forms) {
        const form = response.result.forms.find(
          (f) => f.formKey === "service_log",
        );
        setDetails(form?.details || []);
      } else {
        setDetails([]);
      }
    } catch (err) {
      console.error("Service log fetch error:", err);
      toast.error("Failed to load service log");
      setDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceLog();
    selection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, tenantId]);

  const handleBulkPrint = async () => {
    if (selection.selectedCount === 0) {
      toast.error("Select at least one service log entry to print.");
      return;
    }
    setBulkPrintLoading(true);
    const toastId = toast.loading("Preparing service log print…");
    try {
      const result = await printSelectedServiceLogs({
        employeeId,
        ids: selection.selectedRows.map((r) => r.id),
        entriesFallback: selection.selectedRows,
        branding: { companyName, logoUrl },
        employeeName,
        employeeEmail,
      });
      if (!result.ok) {
        toast.error("Pop-up blocked. Allow pop-ups to print.", { id: toastId });
        return;
      }
      toast.success(
        result.printed === 1
          ? "Service log preview opened. Use Print in the new tab."
          : `Service log preview opened (${result.printed} entries). Use Print in the new tab.`,
        { id: toastId },
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to print service log.", { id: toastId });
    } finally {
      setBulkPrintLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createDate || !createServiceType) {
      toast.error("Date and Service Type are required");
      return;
    }
    setIsCreating(true);
    try {
      const response = await request("/service-logs", {
        method: "POST",
        body: JSON.stringify({
          employeeId: parseInt(employeeId),
          date:
            toIsoDate(formatDateForInput(createDate)) ||
            new Date(formatDateForInput(createDate)).toISOString(),
          serviceType: createServiceType,
          note: createNote || "",
        }),
      });
      if (response.status === 1) {
        toast.success("Service log created successfully");
        setCreateDate(displayDateUS(getCurrentLADate()));
        setCreateServiceType("monthly_in_service_training");
        setCreateNote("");
        fetchServiceLog();
      } else {
        toast.error(response.message || "Failed to create service log");
      }
    } catch (err) {
      console.error("Create error:", err);
      toast.error("Failed to create service log");
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditDate(displayDateUS(item.date));
    setEditServiceType(item.serviceType || "monthly_in_service_training");
    setEditNote(item.note || "");
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setIsSaving(true);
    try {
      const response = await request(`/service-logs/${editingId}`, {
        method: "PUT",
        body: JSON.stringify({
          date:
            toIsoDate(formatDateForInput(editDate)) ||
            new Date(formatDateForInput(editDate)).toISOString(),
          serviceType: editServiceType,
          note: editNote || "",
        }),
      });
      if (response.status === 1) {
        toast.success("Service log updated successfully");
        setEditingId(null);
        fetchServiceLog();
      } else {
        toast.error(response.message || "Failed to update service log");
      }
    } catch (err) {
      console.error("Update error:", err);
      toast.error("Failed to update service log");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      const response = await request(`/service-logs/${itemToDelete.id}`, {
        method: "DELETE",
      });
      if (response.status === 1) {
        toast.success("Service log deleted successfully");
        setIsDeleteModalOpen(false);
        setItemToDelete(null);
        fetchServiceLog();
      } else {
        toast.error(response.message || "Failed to delete");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete service log");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSignClick = (item) => {
    if (isViewOnly || isAdmin) return;
    setItemToSign(item);
    setSignName(fullName || "");
    setIsSignModalOpen(true);
  };

  const handleConfirmSign = async () => {
    if (isViewOnly || isAdmin || !itemToSign) return;
    const nameToUse = signName.trim() || fullName || "User";
    const signatureFormatted = formatSignature(nameToUse);
    setIsSigning(true);
    try {
      const now = new Date().toISOString();
      const response = await request(`/service-logs/${itemToSign.id}/sign`, {
        method: "POST",
        body: JSON.stringify({
          applicantSignature: signatureFormatted,
          applicantSignedAt: now,
        }),
      });
      if (response.status === 1) {
        toast.success("Signed successfully");
        setIsSignModalOpen(false);
        setItemToSign(null);
        fetchServiceLog();
      } else {
        toast.error(response.message || "Failed to sign");
      }
    } catch (err) {
      console.error("Sign error:", err);
      toast.error("Failed to sign");
    } finally {
      setIsSigning(false);
    }
  };

  const showSignButton = (item) =>
    !isViewOnly &&
    !isAdmin &&
    !item.applicantSignature &&
    !item.applicantSignedAt;

  const getSignerName = (item) => {
    const emp = item.employee;
    if (emp?.firstName || emp?.lastName) {
      return [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim();
    }
    return item.applicantSignature || "";
  };

  const getSignerInitials = (item) => {
    const name = getSignerName(item);
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2)
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.charAt(0).toUpperCase();
  };

  const getSignerType = (item) => {
    const emp = item.employee;
    return emp?.role || emp?.position || emp?.roleName || null;
  };

  const showActions = isAdmin && !isViewOnly;
  const headTone = DOCUMENT_THEME.tableHead;
  const colCount = showActions ? 6 : 5; // lead (checkbox+sr) + date + type + note + sign [+ actions]

  return (
    <div className="w-full space-y-4">
      {!isViewOnly && (
        <div
          className={`bg-white rounded-xl border shadow-sm overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
        >
          <div
            className={`flex items-center gap-2 h-11 px-4 border-b box-border ${DOCUMENT_THEME.section.head}`}
          >
            <span
              className={`w-0.5 h-4 rounded-full shrink-0 ${DOCUMENT_THEME.section.bar}`}
              aria-hidden
            />
            <h3
              className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${DOCUMENT_THEME.section.title}`}
            >
              Add New Service Log Entry
            </h3>
          </div>

          <form
            onSubmit={handleCreate}
            className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2.5 p-3"
          >
            <div className="flex items-center gap-2 min-w-0">
              <PlusIcon
                size={16}
                className={`shrink-0 ${DOCUMENT_THEME.section.icon}`}
              />
              <InputDatePicker
                name="createDate"
                value={createDate}
                formatSeparator="/"
                onChange={(e) => setCreateDate(displayDateUS(e.target.value))}
                className="w-auto"
                inputClassName={SERVICE_LOG_FIELD.date}
              />
            </div>

            <select
              value={createServiceType}
              onChange={(e) => setCreateServiceType(e.target.value)}
              className={SERVICE_LOG_FIELD.select}
              aria-label="Service type"
            >
              {SERVICE_LOG_TYPES.map((type) => (
                <option key={type} value={type}>
                  {SERVICE_LOG_TYPE_LABELS[type] || type}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={createNote}
              onChange={(e) => setCreateNote(e.target.value)}
              placeholder="Note"
              className={SERVICE_LOG_FIELD.note}
              aria-label="Note"
            />

            <button
              type="submit"
              disabled={isCreating}
              className={`${DOCUMENT_BTN.modalPrimary} shrink-0`}
            >
              {isCreating ? (
                <>
                  <SpinnerIcon size={14} className="animate-spin" />
                  Adding...
                </>
              ) : (
                "Add"
              )}
            </button>
          </form>
        </div>
      )}

      <p className="text-sm text-slate-600 px-0.5">
        Please eSign each month for the service recorded below.
      </p>

      <DocumentBulkActions
        selectedCount={selection.selectedCount}
        loading={bulkPrintLoading}
        onPrint={handleBulkPrint}
      />

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${DOCUMENT_THEME.section.wrap}`}
      >
        <div className="overflow-x-auto">
          <table className="w-full table-fixed min-w-[680px]">
            <thead>
              <tr className={`border-b ${headTone.row}`}>
                <th
                  className={`py-2.5 text-[11px] font-semibold uppercase tracking-wide ${headTone.cell} ${DOCUMENT_COL.logLead}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className={FORM_FIELD.checkbox}
                      checked={selection.allSelectableSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = selection.someSelectableSelected;
                        }
                      }}
                      onChange={(e) =>
                        selection.toggleAllSelectable(e.target.checked)
                      }
                      aria-label="Select all service log entries"
                      disabled={details.length === 0}
                    />
                    <span>Sr.</span>
                  </div>
                </th>
                <th
                  className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${headTone.cell} ${DOCUMENT_COL.logDate}`}
                >
                  Date
                </th>
                <th
                  className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${headTone.cell} ${DOCUMENT_COL.logType}`}
                >
                  Service Type
                </th>
                <th
                  className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${headTone.cell} ${DOCUMENT_COL.logNote}`}
                >
                  Note
                </th>
                <th
                  className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${headTone.cell} ${DOCUMENT_COL.logSign}`}
                >
                  E-Signature
                </th>
                {showActions && (
                  <th
                    className={`px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${headTone.cell} ${DOCUMENT_COL.logActions}`}
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-50">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    <td className={`py-3 ${DOCUMENT_COL.logLead}`}>
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 bg-indigo-50 rounded animate-pulse" />
                        <div className="h-4 w-5 bg-indigo-50 rounded animate-pulse" />
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="h-4 bg-indigo-50 rounded animate-pulse w-24" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="h-4 bg-indigo-50 rounded animate-pulse w-40" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="h-4 bg-indigo-50 rounded animate-pulse w-full" />
                    </td>
                    <td className="px-3 py-3">
                      <div className="h-4 bg-indigo-50 rounded animate-pulse w-32" />
                    </td>
                    {showActions && (
                      <td className="px-3 py-3">
                        <div className="h-4 bg-indigo-50 rounded animate-pulse w-16 ml-auto" />
                      </td>
                    )}
                  </tr>
                ))
              ) : details.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="px-4 py-12 text-center text-slate-500 text-sm"
                  >
                    No service log records yet.
                  </td>
                </tr>
              ) : (
                details.map((item, index) => (
                  <tr
                    key={item.id}
                    className="hover:bg-indigo-50/40 transition-colors"
                  >
                    <td
                      className={`py-2.5 align-middle ${DOCUMENT_COL.logLead}`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className={FORM_FIELD.checkbox}
                          checked={selection.isSelected(item.id)}
                          onChange={(e) =>
                            selection.toggleRow(
                              String(item.id),
                              e.target.checked,
                            )
                          }
                          aria-label={`Select service log ${index + 1}`}
                        />
                        <span className="min-w-[1.25rem] text-slate-600 text-sm tabular-nums">
                          {index + 1}
                        </span>
                      </div>
                    </td>
                    <td
                      className={`px-3 py-2.5 text-sm text-slate-800 ${DOCUMENT_COL.logDate}`}
                    >
                      {editingId === item.id ? (
                        <InputDatePicker
                          name="editDate"
                          value={editDate}
                          formatSeparator="/"
                          onChange={(e) =>
                            setEditDate(displayDateUS(e.target.value))
                          }
                          className=""
                          inputClassName={SERVICE_LOG_FIELD.rowInput}
                          compact
                        />
                      ) : (
                        displayDateUS(item.date) || "—"
                      )}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-sm text-slate-800 ${DOCUMENT_COL.logType}`}
                    >
                      {editingId === item.id ? (
                        <select
                          value={editServiceType}
                          onChange={(e) => setEditServiceType(e.target.value)}
                          className={SERVICE_LOG_FIELD.rowSelect}
                        >
                          {SERVICE_LOG_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {SERVICE_LOG_TYPE_LABELS[type] || type}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="truncate block">
                          {SERVICE_LOG_TYPE_LABELS[item.serviceType] ||
                            item.serviceType ||
                            "—"}
                        </span>
                      )}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-sm text-slate-700 ${DOCUMENT_COL.logNote}`}
                    >
                      {editingId === item.id ? (
                        <input
                          type="text"
                          value={editNote}
                          onChange={(e) => setEditNote(e.target.value)}
                          className={SERVICE_LOG_FIELD.rowInput}
                        />
                      ) : (
                        <span className="truncate block">
                          {item.note || "—"}
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 ${DOCUMENT_COL.logSign}`}>
                      {editingId === item.id ? (
                        <button
                          type="button"
                          onClick={handleSaveEdit}
                          disabled={isSaving}
                          className={DOCUMENT_BTN.primary}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                      ) : item.applicantSignature ? (
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-xs">
                            {getSignerInitials(item)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="text-slate-800 text-sm font-medium truncate capitalize">
                                {getSignerName(item)}
                              </span>
                              {getSignerType(item) && (
                                <span
                                  className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium uppercase ${DOCUMENT_ROLE_CHIP}`}
                                >
                                  {getSignerType(item)}
                                </span>
                              )}
                            </div>
                            <div className="text-slate-500 text-[11px] mt-0.5 tabular-nums">
                              {formatLATimestampForDate(
                                item.applicantSignedAt,
                              ) || "—"}
                            </div>
                          </div>
                        </div>
                      ) : showSignButton(item) ? (
                        <button
                          type="button"
                          onClick={() => handleSignClick(item)}
                          className={DOCUMENT_BTN.primary}
                        >
                          Click to Sign
                        </button>
                      ) : (
                        <span className="text-slate-400 text-sm">—</span>
                      )}
                    </td>
                    {showActions && (
                      <td
                        className={`px-3 py-2.5 text-right ${DOCUMENT_COL.logActions}`}
                      >
                        {editingId === item.id ? (
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className={DOCUMENT_BTN.outline}
                          >
                            Cancel
                          </button>
                        ) : (
                          <div className="inline-flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className={DOCUMENT_BTN.soft}
                              title="Edit"
                              aria-label="Edit service log"
                            >
                              <EditIcon size={13} />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item)}
                              className={DOCUMENT_BTN.danger}
                              title="Delete"
                              aria-label="Delete service log"
                            >
                              <DeleteIcon size={13} />
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Service Log"
        message={
          itemToDelete
            ? "Are you sure you want to delete this service log entry? This action cannot be undone."
            : ""
        }
        isLoading={isDeleting}
      />

      {isSignModalOpen && (
        <div
          className={DOCUMENT_MODAL.backdrop}
          role="dialog"
          aria-modal="true"
          aria-labelledby="service-log-sign-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !isSigning) {
              setIsSignModalOpen(false);
            }
          }}
        >
          <div className={`${DOCUMENT_MODAL.panel} max-w-md`}>
            <div className={DOCUMENT_MODAL.head}>
              <div className="min-w-0">
                <h3
                  id="service-log-sign-title"
                  className={DOCUMENT_MODAL.title}
                >
                  Electronic Signature
                </h3>
                <p className={DOCUMENT_MODAL.subtitle}>
                  Type your full name to sign this document legally.
                </p>
              </div>
            </div>

            <div className={DOCUMENT_MODAL.body}>
              <label
                className={FORM_FIELD.label}
                htmlFor="service-log-sign-name"
              >
                Your Name
              </label>
              <input
                id="service-log-sign-name"
                type="text"
                value={signName}
                disabled
                onChange={(e) => setSignName(e.target.value)}
                className={`${FORM_FIELD.control} ${FORM_FIELD.disabled}`}
                placeholder="Full Name"
                autoFocus
              />
              <p className="text-slate-400 text-xs mt-1.5">
                Name is automatically retrieved from your login profile.
              </p>
            </div>

            <div className={DOCUMENT_MODAL.footer}>
              <button
                type="button"
                onClick={() => !isSigning && setIsSignModalOpen(false)}
                disabled={isSigning}
                className={DOCUMENT_BTN.modalCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSign}
                disabled={isSigning || !signName.trim()}
                className={DOCUMENT_BTN.modalPrimary}
              >
                {isSigning ? (
                  <>
                    <SpinnerIcon size={14} className="animate-spin" />
                    Signing...
                  </>
                ) : (
                  "Sign Document"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceLog;
